import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  LinearProgress,
  Alert,
  Collapse,
  IconButton
} from '@mui/material';
import Sidebar from '../../components/sidebars/Sidebar_staff';
import TopNav from '../../components/top_bars/TopNav';

import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline'; // Add this import at the top

const EmailComposer = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [formData, setFormData] = useState({
    to: '',
    cc: '',
    subject: '',
    message: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState({ success: null, message: '' });
  const [previousRecipients, setPreviousRecipients] = useState([]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSending(true);
  setSendStatus({ success: null, message: '' });

  try {
    // Get email and role from localStorage
    const userEmail = localStorage.getItem('email');
    const userRole = localStorage.getItem('role');
    
    // Include them in the form data
    const dataToSend = {
      ...formData,
      from: userEmail,
      role: userRole
    };

    const response = await axios.post('https://internal.myelegante.com/send_email.php', dataToSend);
    setSendStatus({ success: true, message: response.data.message });
    
    // Update previous recipients
    const newRecipients = [
      ...new Set([
        formData.to,
        ...(formData.cc ? formData.cc.split(',').map(email => email.trim()) : []),
        ...previousRecipients
      ])
    ].filter(Boolean);
    setPreviousRecipients(newRecipients);
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to send email';
    setSendStatus({ success: false, message });
  } finally {
    setIsSending(false);
  }
};

  const handleSelectPreviousEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      to: email
    }));
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

       

        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 4,
                width: '100%',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
             <Typography
  variant="h5"
  gutterBottom
  sx={{
    mb: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between' // keeps title on left and button on right
  }}
>
  Compose Email

 <Button
  variant="contained"
  startIcon={<MailOutlineIcon />}
  onClick={() => {
    window.open('https://myelegante.com:2096/', '_blank');
  }}
  sx={{
    backgroundColor: '#C84646',
    '&:hover': {
      backgroundColor: '#b33c3c',
    },
    textTransform: 'none',
    borderRadius: 2,
    ml: 2,
    color: 'white'
  }}
>
  Check Inbox / Send with attachments
</Button>

</Typography>
              <Collapse in={sendStatus.success !== null}>
                <Alert
                  severity={sendStatus.success ? "success" : "error"}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setSendStatus({ success: null, message: '' });
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 3 }}
                >
                  {sendStatus.message}
                </Alert>
              </Collapse>

              {previousRecipients.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Previously used addresses:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {previousRecipients.map((email, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        onClick={() => handleSelectPreviousEmail(email)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          fontSize: '0.75rem'
                        }}
                      >
                        {email}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                 <Box sx={{ width: '100%', maxWidth: '800px' }}> {/* Container to control max width */}
  <TextField
    label="To"
    name="to"
    type="email"
    value={formData.to}
    onChange={handleChange}
    required
    fullWidth
    variant="outlined"
    sx={{ mb: 2 }}
  />

  <TextField
    label="Cc"
    name="cc"
    type="text"
    value={formData.cc}
    onChange={handleChange}
    placeholder="Multiple emails separated by commas"
    fullWidth
    variant="outlined"
    sx={{ mb: 2 }}
  />

  <TextField
    label="Subject"
    name="subject"
    type="text"
    value={formData.subject}
    onChange={handleChange}
    required
    fullWidth
    variant="outlined"
    sx={{ mb: 2 }}
  />

  <TextField
    label="Message"
    name="message"
    value={formData.message}
    onChange={handleChange}
    required
    fullWidth
    multiline
    rows={5}
    variant="outlined"
    sx={{ 
      mb: 2,
      '& .MuiOutlinedInput-root': {
        alignItems: 'flex-start' // Better alignment for multiline
      }
    }}
  />
</Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSending}
                      sx={{
                        backgroundColor: '#C84646',
                        '&:hover': {
                          backgroundColor: '#b33c3c',
                        },
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        px: 4,
                        py: 1
                      }}
                    >
                      {isSending ? 'Sending...' : 'Send Email'}
                    </Button>
                  </Box>

                  {isSending && <LinearProgress />}
                </Box>
              </form>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'grey.100',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Email System</strong> - Designed by <strong>eleganté IT</strong>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EmailComposer;